import { FC, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Card, CardContent, Container, Grid } from '@mui/material';
import cn from 'classnames';

import { getResourceDetails } from '@api/resource';
import Loadable from '@components/Loadable/Loadable';
import { IResource } from '@interfaces/resources';
import {
  fetchResources,
  selectCategories,
  selectCategoryId,
  selectResources,
  selectResourcesError,
  selectResourcesIsLoading,
  setSelectedCategoryId,
  setSubcategoryId,
} from '@store/features/recources/slice';
import useDispatch from '@store/hooks/useDispatch';

import './style.scss';

const ResourcesPage: FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const dispatch = useDispatch();
  const selectedCategoryId = useSelector(selectCategoryId);
  const resources = useSelector(selectResources);
  const categories = useSelector(selectCategories);
  const resourcesIsLoading = useSelector(selectResourcesIsLoading);
  const resourcesError = useSelector(selectResourcesError);
  const [categoryName, setCategoryName] = useState<string>('');
  const [selectedSubcategoryIndex, setSelectedSubcategoryIndex] = useState<number>(-1);
  const subCategories = useMemo(
    () => categories.find(category => category.id === selectedCategoryId)?.categories,
    [categories, selectedCategoryId],
  );

  useEffect(() => {
    setCategoryName(categories.find(category => category.id === selectedCategoryId)?.name || '');
  }, [selectedCategoryId, categories]);

  useEffect(() => {
    setSelectedSubcategoryIndex(-1);
    dispatch(setSelectedCategoryId(Number(categoryId)));

    const selectedCategory = categories.find(category => category.id === Number(categoryId));
    if (selectedCategory?.categories?.length) {
      dispatch(setSubcategoryId(selectedCategory.categories[0].id));
    }
  }, [categoryId, categories, dispatch]);

  useEffect(() => {
    if (selectedSubcategoryIndex === -1) {
      dispatch(fetchResources(Number(categoryId)));
    } else if (subCategories?.length) {
      dispatch(fetchResources(Number(subCategories[selectedSubcategoryIndex].id)));
    }
  }, [categoryId, dispatch, selectedSubcategoryIndex, subCategories]);

  const isResourceHasBody = async (resourceId: number) => {
    const data = await getResourceDetails(resourceId);
    return !!data.data.body;
  };

  const onCardClick = (resource: IResource) => {
    isResourceHasBody(resource.id).then(isHasBody => {
      if (isHasBody) {
        window.location.href = `/resource/${resource.id}?category=${encodeURIComponent(
          JSON.stringify({
            name: categoryName,
            id: selectedCategoryId,
          }),
        )}`;
      } else {
        window.open(resource.link, '_blank');
      }
    });
  };

  return (
    <Container maxWidth="lg" className="page resources-page">
      {subCategories && !!subCategories.length && (
        <span className="resources-page__subcategories">
          <span
            className={cn('resources-page__subcategory', {
              'resources-page__active-subcategory': selectedSubcategoryIndex === -1,
            })}
            onClick={() => setSelectedSubcategoryIndex(-1)}
          >
            ALL
          </span>
          {subCategories?.map((item, index) => (
            <span
              key={index}
              className={cn('resources-page__subcategory', {
                'resources-page__active-subcategory': selectedSubcategoryIndex === index,
              })}
              onClick={() => setSelectedSubcategoryIndex(index)}
            >
              {item.name}
            </span>
          ))}
        </span>
      )}
      <span className="resources-page__title">{categoryName}</span>

      <Loadable error={resourcesError || ''} isLoading={resourcesIsLoading}>
        {resources && resources.length ? (
          <div className="resources-page__cards">
            {resources.map((item, index) => (
              <Grid flex={1} key={index} item>
                <Card className="resources-page__card">
                  <CardContent
                    onClick={() => onCardClick(item)}
                    className="resources-page__card-content"
                  >
                    <div
                      className={cn('resources-page__card-text', {
                        'resources-page__with_pic': !!item.thumbnail,
                      })}
                    >
                      <span className="resources-page__card-title">{item.title}</span>
                      <span className="resources-page__card-subtitle">{item.subtitle}</span>
                    </div>
                    {item.thumbnail && (
                      <div className="resources-page__pic-container">
                        <img src={item.thumbnail} />
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </div>
        ) : (
          <div className="resources-page__no-resource-container">
            <span className="resources-page__no-resource-text">No Resources here yet</span>
          </div>
        )}
      </Loadable>
    </Container>
  );
};

export default ResourcesPage;
